//
// # AgentActivityIndicator / AgentStatusStrip
//
// The two surfaces that report agent activity in the AI Assistant panel.
//
//   AgentStatusStrip        pinned under the panel header, so the panel states
//                           its status even when the transcript is scrolled
//                           away from the indicator.
//   AgentActivityIndicator  at the foot of the transcript, next to the message
//                           the agent is about to answer. Its caller renders it
//                           only once the transcript has a message — with none
//                           it has nothing to sit at the foot of, and doubles
//                           the strip a few hundred pixels below it.
//
// Both render in all three phases. That is deliberate: an indicator that only
// exists while working leaves "not working" as an absence, which is the harder
// half of the problem to read.
//

import React from "react";
import { CheckCircle2 } from "lucide-react";
import "./agentActivity.css";
import {
  type AgentActivity,
  type AgentPhase,
  useElapsedSeconds,
} from "./agentActivity";

// Shared copy, so the two surfaces can never disagree about the phase.
function phaseCopy(phase: AgentPhase): string {
  switch (phase) {
    case "working":
      return "Agent working";
    case "done":
      return "Agent finished";
    case "idle":
      return "Agent idle";
  }
}

function stripClass(phase: AgentPhase): string {
  switch (phase) {
    case "working":
      return "agent-status-strip agent-status-strip-working";
    case "done":
      return "agent-status-strip agent-status-strip-done";
    case "idle":
      return "agent-status-strip agent-status-strip-idle";
  }
}

// Pulsing dot with an expanding halo behind it. `size` is the dot diameter.
function PulseDot({ size }: { readonly size: number }) {
  return (
    <span
      className="relative inline-flex shrink-0"
      style={{ width: size, height: size }}
    >
      <span className="agent-halo" />
      <span
        className="agent-dot agent-dot-working relative"
        style={{ width: size, height: size }}
      />
    </span>
  );
}

function PhaseGlyph({
  phase,
  size,
}: {
  readonly phase: AgentPhase;
  readonly size: number;
}) {
  if (phase === "done") return <CheckCircle2 size={size + 4} />;
  if (phase === "idle") {
    return (
      <span
        className="agent-dot agent-dot-idle"
        style={{ width: size, height: size }}
      />
    );
  }
  return <PulseDot size={size} />;
}

// ------------------------------------------------------------------ //
// Status strip                                                        //
// ------------------------------------------------------------------ //

export function AgentStatusStrip({
  activity,
}: {
  readonly activity: AgentActivity;
}) {
  const seconds = useElapsedSeconds(activity);
  const { phase } = activity;
  return (
    <div className="shrink-0">
      <div className={stripClass(phase)}>
        <PhaseGlyph phase={phase} size={8} />
        <span>{phaseCopy(phase)}</span>
        {phase === "working" && (
          <span className="ml-auto tabular-nums">{seconds}s</span>
        )}
        {phase === "idle" && (
          <span className="ml-auto normal-case tracking-normal font-normal opacity-70">
            Ready for a prompt
          </span>
        )}
      </div>
      {/* The sweep only means anything while a run is live, but the track keeps
          its height in every phase so the panel does not shift on transition. */}
      <div
        className={
          phase === "working" ? "agent-progress-track h-1" : "h-1 opacity-0"
        }
      >
        {phase === "working" && <div className="agent-progress-bar" />}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ //
// Transcript indicator                                                //
// ------------------------------------------------------------------ //

export function AgentActivityIndicator({
  activity,
  thinkingWord,
}: {
  readonly activity: AgentActivity;
  // Rotating "still working" word owned by AiAssistant, reused here as the
  // subtitle so the panel keeps its existing voice.
  readonly thinkingWord: string;
}) {
  const seconds = useElapsedSeconds(activity);
  const { phase } = activity;

  if (phase === "working") {
    return (
      <div
        className="self-stretch flex items-center gap-2.5 rounded-md px-3 py-2 font-mono
                   bg-[var(--color-ide-agent-working)] text-[var(--color-ide-agent-on-strong)]"
      >
        <PulseDot size={9} />
        <span className="font-bold uppercase tracking-wider text-xs">
          Agent working
        </span>
        <span className="opacity-80 text-xs">{thinkingWord}…</span>
        <span className="ml-auto tabular-nums font-bold text-xs">
          {seconds}s
        </span>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div
        className="agent-settle self-stretch flex items-center gap-2.5 rounded-md px-3 py-2 font-mono
                   bg-[var(--color-ide-agent-done)] text-[var(--color-ide-agent-on-strong)]"
      >
        <CheckCircle2 size={15} className="shrink-0" />
        <span className="font-bold uppercase tracking-wider text-xs">
          Agent finished
        </span>
      </div>
    );
  }

  return (
    <div
      className="self-stretch flex items-center gap-2.5 rounded-md px-3 py-2 font-mono
                 bg-[var(--color-ide-surface-2)] text-[var(--color-ide-text-muted)]
                 border border-[var(--color-ide-border)]"
    >
      <span className="agent-dot agent-dot-idle w-2.5 h-2.5" />
      <span className="font-bold uppercase tracking-wider text-xs">
        Agent idle
      </span>
      <span className="ml-auto text-xs opacity-70">Ready for a prompt</span>
    </div>
  );
}
